import {
  type BackupScope,
  type CheckCdcPrerequisitesResponse,
  type Condition,
  type UpdateControlViolationInput,
  type Configuration,
  type CreateBackupZeroEtlPolicyInput,
  type Environment,
  type NetworkClassification,
  type ApplicationOverride,
  type InventoryResource,
  type UpdateCloudResourceConfigurationRequest,
  type UpdateBackupZeroEtlPolicyInput,
  type ListInventoryRequest,
  type ListInventoryResourcesResponse,
  type ListResourcesResponse,
  type DynamoDbBackupConfig,
  type KeyspacesBackupConfig,
  type MongoDbBackupConfig,
  type LimitData,
  type DynamoDBRestoreMethodAvailabilityRequest,
  ResourceType,
  ResourceSortField,
  ListAllRegionsApi,
} from '@repo/api-gw-sdk';
import {
  GetSnapshotsSummaryApi,
  SnapshotsApi,
  ResourcesApi,
  ListInventoryApi,
  ListInventoryItemSnapshotsApi,
  ListInventoryAppsApi,
  ListInventorySourceRegionsApi,
  ListInventorySubnetsApi,
  ListInventoryNetworksApi,
  ListInventoryGlobalDatabasesApi,
  ListInventoryBackupRegionsApi,
  ListInventoryResourceTypesApi,
  ListInventoryEnvironmentsApi,
  ListControlViolationsApi,
  UpdateControlViolationApi,
  ListClassificationCategoriesApi,
  ListDataClassificationEntitiesApi,
  ListInventoryClassificationEntitiesApi,
  OverrideApplicationsApi,
  DeleteApplicationOverrideApi,
  ListKnownAppsApi,
  CreateScanJobApi,
  ListSensitivityAnnotationsApi,
  ListInventoryResourceGroupsApi,
  ListInventoryTagsApi,
  ListInventorySourceAccountTagsApi,
  GetResourceConfigurationApi,
  UpdateResourceConfigurationApi,
  ListInventoryResourceIdentifiersApi,
  UpdateShouldFullBackupResourceApi,
  GetBackupScopeApi,
  SaveBackupScopeApi,
  SaveDynamoDbBackupConfigApi,
  DeleteDynamoDbBackupConfigApi,
  ListDynamoDbBackupConfigsApi,
  GetDynamoDbBackupConfigApplicationApi,
  SaveKeyspacesBackupConfigApi,
  DeleteKeyspacesBackupConfigApi,
  ListKeyspacesBackupConfigsApi,
  GetKeyspacesBackupConfigApplicationApi,
  ListMongoDbBackupConfigsApi,
  SaveMongoDbBackupConfigApi,
  DeleteMongoDbBackupConfigApi,
  LimitsApi,
  SetupAwsPrivateLinkApi,
  TeardownAwsPrivateLinkApi,
  OnboardRdsCdcApi,
  BatchValidateResourceConfigurationApi,
  ReassessActionRequiredApi,
  CheckCdcPrerequisitesApi,
  BackupZeroEtlPoliciesApi,
  GetTotalSourceStorageSizeApi,
} from '@repo/api-gw-sdk';
import type { ColumnSort, PaginationState } from '@tanstack/react-table';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { convertQuery, convertSort } from '@/utils/conditionQuery';
import { encodePageToken } from '@/utils/string';

import type { HttpClient } from './httpClient';

import { ApplicationsLogosWithAliases } from '../inventory/data';

const inventoryBaseUrl = '/inventory';
const inventorySnapshotsBaseUrl = 'snapshots';

export const inventory = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const listInventoryApi = new ListInventoryApi(config);
  const resourcesApi = new ResourcesApi(config);
  const listInventoryItemSnapshotsApi = new ListInventoryItemSnapshotsApi(
    config
  );
  const listInventoryAppsApi = new ListInventoryAppsApi(config);
  const listInventoryTagsApi = new ListInventoryTagsApi(config);
  const listInventorySourceAccountTagsApi =
    new ListInventorySourceAccountTagsApi(config);
  const listInventorySubnetsApi = new ListInventorySubnetsApi(config);
  const listInventoryNetworksApi = new ListInventoryNetworksApi(config);
  const listInventoryGlobalDatabasesApi = new ListInventoryGlobalDatabasesApi(
    config
  );
  const listInventoryEnvironmentsApi = new ListInventoryEnvironmentsApi(config);
  const listInventoryBackupRegionsApi = new ListInventoryBackupRegionsApi(
    config
  );
  const listDataClassificationEntitiesApi =
    new ListDataClassificationEntitiesApi(config);
  const listInventoryClassificationEntitiesApi =
    new ListInventoryClassificationEntitiesApi(config);
  const listClassificationCategoriesApi = new ListClassificationCategoriesApi(
    config
  );
  const listInventoryResourceTypesApi = new ListInventoryResourceTypesApi(
    config
  );
  const listInventorySourceRegionsApi = new ListInventorySourceRegionsApi(
    config
  );
  const listAllRegionsApi = new ListAllRegionsApi(config);
  const listControlViolationsApi = new ListControlViolationsApi(config);
  const updateControlViolationApi = new UpdateControlViolationApi(config);

  const overrideApplicationsApi = new OverrideApplicationsApi(config);
  const deleteApplicationOverrideApi = new DeleteApplicationOverrideApi(config);
  const listKnownAppsApi = new ListKnownAppsApi(config);

  const createScanJobApi = new CreateScanJobApi(config);
  const snapshotApi = new SnapshotsApi(config);
  const getResourceConfigurationApi = new GetResourceConfigurationApi(config);
  const updateResourceConfigurationApi = new UpdateResourceConfigurationApi(
    config
  );
  const listInventoryResourceGroupsApi = new ListInventoryResourceGroupsApi(
    config
  );
  const listInventoryResourceIdentifiersApi =
    new ListInventoryResourceIdentifiersApi(config);
  const updateShouldFullBackupResourceApi =
    new UpdateShouldFullBackupResourceApi(config);
  const getBackupScopeApi = new GetBackupScopeApi(config);
  const saveBackupScopeApi = new SaveBackupScopeApi(config);
  const listDynamoDbBackupConfigsApi = new ListDynamoDbBackupConfigsApi(config);
  const saveDynamoDbBackupConfigApi = new SaveDynamoDbBackupConfigApi(config);
  const getDynamoDbBackupConfigApplicationApi =
    new GetDynamoDbBackupConfigApplicationApi(config);
  const deleteDynamoDbBackupConfigApi = new DeleteDynamoDbBackupConfigApi(
    config
  );
  const listKeyspacesBackupConfigsApi = new ListKeyspacesBackupConfigsApi(
    config
  );
  const saveKeyspacesBackupConfigApi = new SaveKeyspacesBackupConfigApi(config);
  const getKeyspacesBackupConfigApplicationApi =
    new GetKeyspacesBackupConfigApplicationApi(config);
  const deleteKeyspacesBackupConfigApi = new DeleteKeyspacesBackupConfigApi(
    config
  );
  const listMongoDbBackupConfigsApi = new ListMongoDbBackupConfigsApi(config);
  const saveMongoDbBackupConfigApi = new SaveMongoDbBackupConfigApi(config);
  const deleteMongoDbBackupConfigApi = new DeleteMongoDbBackupConfigApi(config);
  const limitsApi = new LimitsApi(config);
  const batchValidateResourceConfigurationApi =
    new BatchValidateResourceConfigurationApi(config);
  const reassessActionRequiredApi = new ReassessActionRequiredApi(config);
  const listSensitivityAnnotationsApi = new ListSensitivityAnnotationsApi(
    config
  );
  const getSnapshotsSummaryApi = new GetSnapshotsSummaryApi(config);
  const setupAwsPrivateLinkApi = new SetupAwsPrivateLinkApi(config);
  const teardownAwsPrivateLinkApi = new TeardownAwsPrivateLinkApi(config);
  const onboardRdsCdcApi = new OnboardRdsCdcApi(config);
  const checkCdcPrerequisitesApi = new CheckCdcPrerequisitesApi(config);
  const backupZeroEtlPoliciesApi = new BackupZeroEtlPoliciesApi(config);
  const getTotalSourceStorageSizeApi = new GetTotalSourceStorageSizeApi(config);

  const resourceSortFieldMap: Record<string, ResourceSortField> = {
    id: ResourceSortField.Id,
    backupStatus: ResourceSortField.BackupStatus,
    accountId: ResourceSortField.AccountId,
    resourceName: ResourceSortField.ResourceName,
    environment: ResourceSortField.Environment,
    // column name → API sort field aliases
    resourceId: ResourceSortField.ProviderResourceId,
    providerResourceId: ResourceSortField.ProviderResourceId,
    sourceStorageSize: ResourceSortField.SizeBytes,
    sizeBytes: ResourceSortField.SizeBytes,
    backupStorageSize: ResourceSortField.SnapshotStorageSizeBytes,
    snapshotStorageSizeBytes: ResourceSortField.SnapshotStorageSizeBytes,
    eonSnapshotCount: ResourceSortField.EonSnapshotCount,
    genericSnapshotsDistinctDateCount: ResourceSortField.NonEonSnapshotCount,
    nonEonSnapshotCount: ResourceSortField.NonEonSnapshotCount,
    latestSnapshotTime: ResourceSortField.LatestSnapshotTime,
    oldestSnapshotTime: ResourceSortField.OldestSnapshotTime,
    dateDiscovered: ResourceSortField.DateDiscovered,
    createdTime: ResourceSortField.CreatedTime,
    resourceGroupName: ResourceSortField.ResourceGroupName,
    clusterName: ResourceSortField.ClusterName,
    globalClusterIdentifier: ResourceSortField.GlobalClusterIdentifier,
  };

  return {
    download: (columnFilters?: Condition, columns?: string) =>
      listInventoryApi.inventoryDownload(
        currentProjectId,
        columnFilters ? toJsonText(columnFilters) : undefined,
        undefined,
        columns
      ),
    useList: (
      pagination: PaginationState,
      sorting: ColumnSort,
      columnFilters: Condition | undefined,
      swrOptions?: { refreshInterval?: number; enabled?: boolean },
      includeSaas = false
    ) => {
      const { enabled = true, ...restSwr } = swrOptions ?? {};
      const sortString = `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`;
      const request: ListInventoryRequest = {
        sorts: convertSort(sortString, resourceSortFieldMap),
        filters: convertQuery(columnFilters),
      };
      return httpClient.executeWithConfig(
        (...args: Parameters<typeof resourcesApi.listResources>) =>
          resourcesApi
            .listResources(
              args[0],
              pagination.pageIndex > 0
                ? encodePageToken(pagination.pageIndex)
                : undefined,
              args[2],
              includeSaas,
              request
            )
            .then(normalizeInventoryList),
        enabled ? inventoryBaseUrl : null,
        '',
        restSwr,
        currentProjectId,
        `${pagination.pageIndex}`,
        pagination.pageSize,
        includeSaas,
        request
      );
    },
    // Legacy list entrypoint that passes the full condition tree as serialised JSON.
    // Used when the condition contains OR combinators that the flat InventoryFilterConditions
    // struct (used by listResources) cannot represent. This will be replaced by a dedicated
    // server-side implementation in the future.
    useOldList: (
      pagination: PaginationState,
      sorting: ColumnSort,
      columnFilters: Condition | undefined,
      swrOptions?: { refreshInterval?: number }
    ) => {
      const sortString = `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`;
      const filter = columnFilters ? toJsonText(columnFilters) : undefined;
      return httpClient.executeWithConfig(
        (
          projectId: string,
          pageIndex: number,
          pageSize: number,
          sort: string,
          f: string | undefined
        ) =>
          listInventoryApi
            .listInventory(projectId, pageIndex, pageSize, sort, f)
            .then(normalizeOldInventoryList)
            .then(normalizeInventoryList),
        inventoryBaseUrl,
        'legacy',
        swrOptions ?? {},
        currentProjectId,
        pagination.pageIndex,
        pagination.pageSize,
        sortString,
        filter
      );
    },
    useTotalSourceStorageSize: (filter: Condition | undefined) =>
      httpClient.execute(
        getTotalSourceStorageSizeApi.getTotalSourceStorageSize.bind(
          getTotalSourceStorageSizeApi
        ),
        inventoryBaseUrl,
        'total-source-storage-size',
        currentProjectId,
        filter ? { filter: filter } : undefined
      ),
    useListResourceIdentifiers: (
      filter?: Condition,
      includeSaas?: boolean,
      enabled = true
    ) =>
      httpClient.execute(
        listInventoryResourceIdentifiersApi.listInventoryResourceIdentifiers.bind(
          listInventoryResourceIdentifiersApi
        ),
        // null baseUrl nulls the SWR key, skipping the fetch when the caller has
        // nothing to resolve — avoids an unfiltered all-resources request.
        enabled ? inventoryBaseUrl : null,
        'resource-identifiers',
        currentProjectId,
        undefined,
        undefined,
        filter ? toJsonText(filter) : undefined,
        includeSaas
      ),
    useResource: (id: string) =>
      httpClient.execute(
        (...args: Parameters<typeof resourcesApi.getResource>) =>
          resourcesApi.getResource(...args).catch((x) => {
            if (x.code !== 404) {
              throw x;
            }
          }),
        inventoryBaseUrl,
        '',
        currentProjectId,
        id
      ),
    getWithoutHook: (id: string) =>
      resourcesApi.getResource(currentProjectId, id),
    useMultiple: (ids: string[]) =>
      httpClient.execute(
        (projectId: string, ids: string[]) =>
          Promise.all(ids.map((id) => resourcesApi.getResource(projectId, id))),
        inventoryBaseUrl,
        '',
        currentProjectId,
        ids
      ),
    snapshots: {
      useSummary: (resourceId: string) =>
        httpClient.execute(
          getSnapshotsSummaryApi.getSnapshotsSummary.bind(
            getSnapshotsSummaryApi
          ),
          inventoryBaseUrl,
          'snapshots-summary',
          currentProjectId,
          resourceId
        ),
      hold: (snapshotId: string) =>
        snapshotApi.holdSnapshot(currentProjectId, snapshotId),
      removeHold: (snapshotId: string) =>
        snapshotApi.removeSnapshotHold(currentProjectId, snapshotId),
      useList: (
        resourceId: string,
        pageIndex: number,
        pageSize: number,
        start?: Date,
        end?: Date
      ) => {
        return httpClient.execute(
          listInventoryItemSnapshotsApi.listInventoryItemSnapshots.bind(
            listInventoryItemSnapshotsApi
          ),
          inventoryBaseUrl,
          inventorySnapshotsBaseUrl,
          currentProjectId,
          resourceId,
          pageIndex,
          pageSize,
          'eon',
          start,
          end
        );
      },
      useSnapshot: (snapshotId?: string) =>
        httpClient.execute(
          snapshotApi.getSnapshot.bind(snapshotApi),
          snapshotId ? inventoryBaseUrl : null,
          'snapshot',
          currentProjectId,
          snapshotId || ''
        ),
      rds: {
        usePitrWindow: (resourceId: string, resourceType: ResourceType) =>
          httpClient.execute(
            (...args: Parameters<typeof snapshotApi.getRdsNativePitrWindow>) =>
              snapshotApi
                .getRdsNativePitrWindow(...args)
                // until server will stop throwing 400 for no native window.
                .catch(() => undefined),
            resourceType === ResourceType.AwsRds ? inventoryBaseUrl : null,
            '/rds-native-pitr-window',
            currentProjectId,
            resourceId
          ),
      },
      azureSqlDatabase: {
        // `enabled` lets the caller gate on both the resource type and the feature
        // flag so we don't make an Azure round-trip for databases the feature
        // doesn't apply to.
        usePitrWindow: (resourceId: string, enabled: boolean) =>
          httpClient.execute(
            (
              ...args: Parameters<
                typeof snapshotApi.getAzureSqlDatabaseNativePitrWindow
              >
            ) =>
              snapshotApi
                .getAzureSqlDatabaseNativePitrWindow(...args)
                // 404/400 when the database has no enforced PITR window yet.
                .catch(() => undefined),
            enabled ? inventoryBaseUrl : null,
            '/azure-sql-database-native-pitr-window',
            currentProjectId,
            resourceId
          ),
        // `enabled` lets the caller defer the lookup until the user opts to pick
        // a non-source target server.
        useRestoreTargetServers: (resourceId: string, enabled: boolean) =>
          httpClient.execute(
            (
              ...args: Parameters<
                typeof snapshotApi.listAzureSqlRestoreTargetServers
              >
            ) => snapshotApi.listAzureSqlRestoreTargetServers(...args),
            enabled ? inventoryBaseUrl : null,
            '/azure-sql-restore-target-servers',
            currentProjectId,
            resourceId
          ),
      },
      usePitrRestoreWindows: (
        resourceId: string,
        startTime?: Date,
        endTime?: Date
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof snapshotApi.getPitrRestoreWindows>) =>
            snapshotApi.getPitrRestoreWindows(...args),
          inventoryBaseUrl,
          '/pitr-restore-windows',
          currentProjectId,
          resourceId,
          startTime,
          endTime
        ),
      dynamoDb: {
        useRestoreMethodAvailability: (
          snapshotId: string,
          request: DynamoDBRestoreMethodAvailabilityRequest
        ) =>
          httpClient.execute(
            (
              ...args: Parameters<
                typeof snapshotApi.dynamoDBRestoreMethodAvailability
              >
            ) =>
              snapshotApi
                .dynamoDBRestoreMethodAvailability(...args)
                .catch((err) => {
                  if (err.code !== 403) {
                    throw err;
                  }
                  return { available: false };
                }),
            inventoryBaseUrl,
            'RestoreMethodAvailability',
            currentProjectId,
            snapshotId,
            request
          ),
      },
    },
    tags: {
      useList: (filter?: Condition, enabled = true) =>
        httpClient.execute(
          listInventoryTagsApi.listInventoryTags.bind(listInventoryTagsApi),
          enabled ? inventoryBaseUrl : null,
          '/tags',
          currentProjectId,
          undefined,
          undefined,
          filter ? toJsonText(filter) : undefined
        ),
    },
    sourceAccountTags: {
      useList: (filter?: Condition, enabled = true) =>
        httpClient.execute(
          listInventorySourceAccountTagsApi.listInventorySourceAccountTags.bind(
            listInventorySourceAccountTagsApi
          ),
          enabled ? inventoryBaseUrl : null,
          '/source-account-tags',
          currentProjectId,
          undefined,
          undefined,
          filter ? toJsonText(filter) : undefined
        ),
    },
    entities: {
      useList: () => {
        const { EnableInventoryEntitiesFilter } = useFeatureFlags();
        return httpClient.execute(
          listInventoryClassificationEntitiesApi.listInventoryClassificationEntities.bind(
            listInventoryClassificationEntitiesApi
          ),
          EnableInventoryEntitiesFilter ? inventoryBaseUrl : null,
          '/classification-entities',
          currentProjectId
        );
      },
    },
    apps: {
      useList: () =>
        httpClient.execute(
          listInventoryAppsApi.listInventoryApps.bind(listInventoryAppsApi),
          inventoryBaseUrl,
          '/apps',
          currentProjectId
        ),
      useKnownList: () =>
        httpClient.execute(
          listKnownAppsApi.listKnownApps.bind(listKnownAppsApi),
          inventoryBaseUrl,
          '/known-applications',
          currentProjectId
        ),
      override: (resourceIds: string[], apps: string[]) => {
        return overrideApplicationsApi.overrideApplications(currentProjectId, {
          resourceIds,
          applications: apps,
        });
      },
      async deleteOverride(resources: InventoryResource[]) {
        const payload = resources.flatMap((res) =>
          (res.classifications?.appsDetails?.apps || []).map((app) => ({
            resourceId: res.id,
            name: app.name,
          }))
        ) satisfies ApplicationOverride[];

        return deleteApplicationOverrideApi.deleteApplicationOverride(
          currentProjectId,
          payload
        );
      },
    },
    regions: {
      useList: () =>
        httpClient.execute(
          listInventorySourceRegionsApi.listInventorySourceRegions.bind(
            listInventorySourceRegionsApi
          ),
          inventoryBaseUrl,
          '/source-regions',
          currentProjectId
        ),
      useListAll: () =>
        httpClient.execute(
          listAllRegionsApi.listAllRegions.bind(listAllRegionsApi),
          inventoryBaseUrl,
          '/all-regions'
        ),
    },
    subnets: {
      useList: () =>
        httpClient.execute(
          listInventorySubnetsApi.listInventorySubnets.bind(
            listInventorySubnetsApi
          ),
          inventoryBaseUrl,
          '/subnets',
          currentProjectId
        ),
    },
    resourceGroups: {
      useList: () =>
        httpClient.execute(
          listInventoryResourceGroupsApi.listInventoryResourceGroups.bind(
            listInventoryResourceGroupsApi
          ),
          inventoryBaseUrl,
          '/resource-groups',
          currentProjectId
        ),
    },
    networks: {
      useList: () =>
        httpClient.execute(
          listInventoryNetworksApi.listInventoryNetworks.bind(
            listInventoryNetworksApi
          ),
          inventoryBaseUrl,
          '/networks',
          currentProjectId
        ),
    },
    globalDatabases: {
      useList: () =>
        httpClient.execute(
          listInventoryGlobalDatabasesApi.listInventoryGlobalDatabases.bind(
            listInventoryGlobalDatabasesApi
          ),
          inventoryBaseUrl,
          '/global-databases',
          currentProjectId
        ),
    },
    resourceTypes: {
      useList: (includeSaas?: boolean) =>
        httpClient.execute(
          listInventoryResourceTypesApi.listInventoryResourceTypes.bind(
            listInventoryResourceTypesApi
          ),
          inventoryBaseUrl,
          '/resource-types',
          currentProjectId,
          includeSaas
        ),
    },
    environments: {
      useList: () =>
        httpClient.execute(
          listInventoryEnvironmentsApi.listInventoryEnvironments.bind(
            listInventoryEnvironmentsApi
          ),
          inventoryBaseUrl,
          '/environments',
          currentProjectId
        ),
      override: (resourceId: string, environment: Environment) => {
        return resourcesApi.overrideEnvironment(currentProjectId, resourceId, {
          environment,
        });
      },
      deleteOverride: (resourceId: string) => {
        return resourcesApi.removeEnvironmentOverride(
          currentProjectId,
          resourceId
        );
      },
    },
    networkClassifications: {
      override: (
        resourceId: string,
        networkClassification: NetworkClassification
      ) => {
        return resourcesApi.overrideNetworkClassification(
          currentProjectId,
          resourceId,
          { networkClassification }
        );
      },
      deleteOverride: (resourceId: string) => {
        return resourcesApi.removeNetworkClassificationOverride(
          currentProjectId,
          resourceId
        );
      },
    },
    dataClasses: {
      useList: (resourceId: string) =>
        httpClient.execute(
          listDataClassificationEntitiesApi.listDataClassificationEntities.bind(
            listDataClassificationEntitiesApi
          ),
          inventoryBaseUrl,
          'classifications',
          currentProjectId,
          resourceId
        ),
      useCategories: () =>
        httpClient.execute(
          listClassificationCategoriesApi.listClassificationCategories.bind(
            listClassificationCategoriesApi
          ),
          inventoryBaseUrl,
          '/data-classification-categories',
          currentProjectId
        ),
      override: (resourceId: string, payload: string[]) => {
        return resourcesApi.overrideDataClasses(currentProjectId, resourceId, {
          dataClasses: payload,
        });
      },
      deleteOverride: (resourceId: string) => {
        return resourcesApi.removeDataClassesOverride(
          currentProjectId,
          resourceId
        );
      },
    },
    sensitivityAnnotations: {
      useList: (resourceId: string) =>
        httpClient.execute(
          listSensitivityAnnotationsApi.listSensitivityAnnotations.bind(
            listSensitivityAnnotationsApi
          ),
          inventoryBaseUrl,
          '/sensitivity-annotations',
          currentProjectId,
          resourceId
        ),
      override: (resourceId: string, sensitivityLevel: string) => {
        return resourcesApi.overrideSensitivityAnnotations(
          currentProjectId,
          resourceId,
          { sensitivityLevel }
        );
      },
      deleteOverride: (resourceId: string) => {
        return resourcesApi.removeSensitivityAnnotationsOverride(
          currentProjectId,
          resourceId
        );
      },
    },
    backupRegions: {
      useList: () =>
        httpClient.execute(
          listInventoryBackupRegionsApi.listInventoryBackupRegions.bind(
            listInventoryBackupRegionsApi
          ),
          inventoryBaseUrl,
          '/backup-regions',
          currentProjectId
        ),
    },
    backupScope: {
      useList: () =>
        httpClient.execute(
          getBackupScopeApi.getBackupScope.bind(getBackupScopeApi),
          inventoryBaseUrl,
          '/backup-scope',
          currentProjectId
        ),
      save: (backupScope: BackupScope) =>
        saveBackupScopeApi.saveBackupScope(currentProjectId, backupScope),
    },
    violations: {
      useList: (id: string) =>
        httpClient.execute(
          listControlViolationsApi.listControlViolations.bind(
            listControlViolationsApi
          ),
          inventoryBaseUrl,
          '/violations',
          currentProjectId,
          id
        ),
      update: (id: string, payload: UpdateControlViolationInput) =>
        updateControlViolationApi.updateControlViolation(
          currentProjectId,
          id,
          payload
        ),
    },
    configuration: {
      useConfig: (id: string) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof getResourceConfigurationApi.getResourceConfiguration
            >
          ) =>
            getResourceConfigurationApi
              .getResourceConfiguration(...args)
              .catch((x) => {
                if (x.code !== 404) {
                  throw x;
                }
                return undefined;
              }),
          id ? inventoryBaseUrl : null,
          '/configuration',
          currentProjectId,
          id
        ),
      update: (id: string, payload: UpdateCloudResourceConfigurationRequest) =>
        updateResourceConfigurationApi.updateResourceConfiguration(
          currentProjectId,
          id,
          payload
        ),
    },
    resources: {
      excludeResourceFromBackup: (resourceId: string) =>
        resourcesApi.excludeResourceFromBackup(currentProjectId, resourceId),
      cancelResourceBackupExclusion: (resourceId: string) =>
        resourcesApi.cancelResourceBackupExclusion(
          currentProjectId,
          resourceId
        ),
      excludeVolumeFromBackup: (resourceId: string, volumeId: string) =>
        resourcesApi.excludeVolumeFromBackup(
          currentProjectId,
          resourceId,
          volumeId
        ),
      cancelVolumeBackupExclusion: (resourceId: string, volumeId: string) =>
        resourcesApi.cancelVolumeBackupExclusion(
          currentProjectId,
          resourceId,
          volumeId
        ),
      enableResourceFromLimitExceeded: (resourceId: string) =>
        resourcesApi.enableResourceFromLimitExceeded(
          currentProjectId,
          resourceId
        ),
    },
    jobs: {
      createScanJob: (resourceId: string) => {
        return createScanJobApi.createScanJob(currentProjectId, { resourceId });
      },
      forceFullBackupForResource: (resourceId: string) => {
        return updateShouldFullBackupResourceApi.updateShouldFullBackupResource(
          currentProjectId,
          resourceId,
          {
            shouldRunFullBackup: true,
          }
        );
      },
    },
    backupConfiguration: {
      dynamodb: {
        useList: () =>
          httpClient.execute(
            listDynamoDbBackupConfigsApi.listDynamoDbBackupConfigs.bind(
              listDynamoDbBackupConfigsApi
            ),
            inventoryBaseUrl,
            '/backup-configuration/dynamodb',
            currentProjectId
          ),
        useConfig: (resourceId: string) =>
          httpClient.execute(
            (
              ...args: Parameters<
                typeof getDynamoDbBackupConfigApplicationApi.getDynamoDbBackupConfigApplication
              >
            ) =>
              getDynamoDbBackupConfigApplicationApi
                .getDynamoDbBackupConfigApplication(...args)
                .catch((x) => {
                  if (x.code !== 404) {
                    throw x;
                  }
                  return undefined;
                }),
            inventoryBaseUrl,
            '/backup-configuration/dynamodb/get',
            currentProjectId,
            resourceId
          ),
        save: (
          config: DynamoDbBackupConfig,
          cloudAccountId?: string,
          resourceId?: string
        ) =>
          saveDynamoDbBackupConfigApi.saveDynamoDbBackupConfig(
            currentProjectId,
            config,
            cloudAccountId,
            resourceId
          ),
        delete: (cloudAccountId?: string, resourceId?: string) =>
          deleteDynamoDbBackupConfigApi.deleteDynamoDbBackupConfig(
            currentProjectId,
            cloudAccountId,
            resourceId
          ),
      },
      keyspaces: {
        useList: () =>
          httpClient.execute(
            listKeyspacesBackupConfigsApi.listKeyspacesBackupConfigs.bind(
              listKeyspacesBackupConfigsApi
            ),
            inventoryBaseUrl,
            '/backup-configuration/keyspaces',
            currentProjectId
          ),
        useConfig: (resourceId: string) =>
          httpClient.execute(
            (
              ...args: Parameters<
                typeof getKeyspacesBackupConfigApplicationApi.getKeyspacesBackupConfigApplication
              >
            ) =>
              getKeyspacesBackupConfigApplicationApi
                .getKeyspacesBackupConfigApplication(...args)
                .catch((x) => {
                  if (x.code !== 404) {
                    throw x;
                  }
                  return undefined;
                }),
            inventoryBaseUrl,
            '/backup-configuration/keyspaces/get',
            currentProjectId,
            resourceId
          ),
        save: (
          config: KeyspacesBackupConfig,
          cloudAccountId?: string,
          resourceId?: string
        ) =>
          saveKeyspacesBackupConfigApi.saveKeyspacesBackupConfig(
            currentProjectId,
            config,
            cloudAccountId,
            resourceId
          ),
        delete: (cloudAccountId?: string, resourceId?: string) =>
          deleteKeyspacesBackupConfigApi.deleteKeyspacesBackupConfig(
            currentProjectId,
            cloudAccountId,
            resourceId
          ),
      },
      mongodb: {
        useList: () =>
          httpClient.execute(
            listMongoDbBackupConfigsApi.listMongoDbBackupConfigs.bind(
              listMongoDbBackupConfigsApi
            ),
            inventoryBaseUrl,
            '/backup-configuration/mongodb',
            currentProjectId
          ),
        save: (
          config: MongoDbBackupConfig,
          cloudAccountId?: string,
          resourceId?: string
        ) =>
          saveMongoDbBackupConfigApi.saveMongoDbBackupConfig(
            currentProjectId,
            config,
            cloudAccountId,
            resourceId
          ),
        delete: (cloudAccountId?: string, resourceId?: string) =>
          deleteMongoDbBackupConfigApi.deleteMongoDbBackupConfig(
            currentProjectId,
            cloudAccountId,
            resourceId
          ),
      },
    },
    limits: {
      useLimit: () =>
        httpClient.execute(
          limitsApi.getProjectLimits.bind(limitsApi),
          inventoryBaseUrl,
          '/limits',
          currentProjectId
        ),
      set: (limits: Record<string, LimitData>) =>
        limitsApi.setProjectLimits(currentProjectId, limits),
      enableResourceOverrideLimits: (resourceId: string) =>
        limitsApi.enableResourceOverrideLimits(currentProjectId, resourceId),
      disableResourceOverrideLimits: (resourceId: string) =>
        limitsApi.disableResourceOverrideLimits(currentProjectId, resourceId),
    },
    attachmentEvents: {
      useList: (resourceId: string) =>
        httpClient.execute(
          resourcesApi.listResourceAttachmentEvents.bind(resourcesApi),
          inventoryBaseUrl,
          '/attachment-events',
          currentProjectId,
          resourceId
        ),
    },
    privateLink: {
      useOnboardRdsCdc: (resourceId: string) =>
        httpClient.execute(
          onboardRdsCdcApi.onboardRdsCdc.bind(onboardRdsCdcApi),
          inventoryBaseUrl,
          '/rds-cdc-onboarding',
          currentProjectId,
          resourceId
        ),
      useCheckCdcPrerequisites: (
        resourceId: string,
        swrOptions: {
          refreshInterval?:
            number | ((latestData?: CheckCdcPrerequisitesResponse) => number);
        } = {}
      ) =>
        httpClient.executeWithConfig(
          checkCdcPrerequisitesApi.checkCdcPrerequisites.bind(
            checkCdcPrerequisitesApi
          ),
          inventoryBaseUrl,
          '/check-cdc-prerequisites',
          swrOptions,
          currentProjectId,
          resourceId
        ),
      setupAwsPrivateLink: (
        resourceId: string,
        request: { ramResourceShareArn: string }
      ) =>
        setupAwsPrivateLinkApi.setupAwsPrivateLink(
          currentProjectId,
          resourceId,
          request
        ),
      teardownAwsPrivateLink: (resourceId: string) =>
        teardownAwsPrivateLinkApi.teardownAwsPrivateLink(
          currentProjectId,
          resourceId
        ),
    },
    batchValidateResourceConfiguration: (resourceIds: string[]) => {
      return batchValidateResourceConfigurationApi.batchValidateResourceConfiguration(
        currentProjectId,
        {
          resourceIds,
        }
      );
    },
    reassessBigQueryActionRequired: (resourceId: string) => {
      return reassessActionRequiredApi.reassessActionRequired(
        currentProjectId,
        {
          resourceId,
          resourceType: ResourceType.GcpBigQuery,
        }
      );
    },
    zeroEtlPolicies: {
      useList: (
        swrOptions?: Parameters<typeof httpClient.executeWithConfig>[3]
      ) =>
        httpClient.executeWithConfig(
          backupZeroEtlPoliciesApi.listBackupZeroEtlPolicies.bind(
            backupZeroEtlPoliciesApi
          ),
          inventoryBaseUrl,
          'listBackupZeroEtlPolicies',
          swrOptions,
          currentProjectId
        ),
      create: (request: CreateBackupZeroEtlPolicyInput) =>
        backupZeroEtlPoliciesApi.createBackupZeroEtlPolicy(
          currentProjectId,
          request
        ),
      update: (
        zeroEtlPolicyId: string,
        request: UpdateBackupZeroEtlPolicyInput
      ) =>
        backupZeroEtlPoliciesApi.updateBackupZeroEtlPolicy(
          currentProjectId,
          zeroEtlPolicyId,
          request
        ),
      delete: (zeroEtlPolicyId: string) =>
        backupZeroEtlPoliciesApi.deleteBackupZeroEtlPolicy(
          currentProjectId,
          zeroEtlPolicyId
        ),
    },
  };
};

function normalizeOldInventoryList(
  response: ListInventoryResourcesResponse
): ListResourcesResponse {
  return {
    resources: response.resources,
    totalCount: response.pagination.recordsCount,
    nextToken: response.pagination.nextToken,
  };
}

function normalizeInventoryList(
  response: ListResourcesResponse
): ListResourcesResponse {
  response.resources.forEach((resource) => {
    if (!resource.classifications) {
      return;
    }
    resource.classifications.appsDetails = {
      apps: resource.classifications?.appsDetails?.apps?.filter((app, i) => {
        // Check if we're the only ones
        const icon = ApplicationsLogosWithAliases[app.name];
        const otherIcons =
          resource.classifications?.appsDetails?.apps?.findIndex(
            (otherApp, j) =>
              i < j && ApplicationsLogosWithAliases[otherApp.name] === icon
          );
        if (otherIcons === -1) {
          return true;
        }
        return false;
      }),
    };
  });

  return response;
}
